import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, Input, Select, Message } from "@arco-design/web-react";

import { REPORT_REASON_IDS, ReportReasonId } from "../../../../constants/reportReasons";
import { getButtonColor, getButtonTextColor } from "../../../../utils/cssVariables";
import './ConversationReportDialog.css';

export type ModerationAction = 'block' | 'report';

type Props = {
  visible: boolean;
  onSubmit: (action: ModerationAction, message: string, category: ReportReasonId) => void;
  onClose: () => void;
}

export function ConversationReportDialog({ visible, onSubmit, onClose }: Props) {
  const { t } = useTranslation();
  const TextArea = Input.TextArea;
  const Option = Select.Option;

  const [selectedAction, setSelectedAction] = useState<ModerationAction | ''>('');
  const [selectedCategory, setSelectedCategory] = useState<ReportReasonId | ''>('');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (!visible) {
      setSelectedAction('');
      setSelectedCategory('');
      setMessage('');
    }
  }, [visible]);

  const handleSubmit = () => {
    if (!selectedAction) {
      Message.error({ content: t('messages.moderation_action_required'), closable: true, duration: 4000 });
      return;
    }
    if (!selectedCategory) {
      Message.error({ content: t('messages.report_user_validation_error'), closable: true, duration: 4000 });
      return;
    }
    if (selectedAction === 'block' && !message.trim()) {
      Message.error({ content: t('messages.block_reason_required'), closable: true, duration: 4000 });
      return;
    }
    onSubmit(selectedAction, message, selectedCategory);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      title={t('messages.block_report_title')}
      footer={null}
      onCancel={onClose}
      className="conversation-report-dialog"
    >
      <form>
        <div className="conversation-report-dialog__content">
          <Select
            placeholder={t('messages.moderation_select_action')}
            onChange={(v) => setSelectedAction(v as ModerationAction)}
            value={selectedAction || undefined}
          >
            <Option value="block">{t('admin.moderation.action_block')}</Option>
            <Option value="report">{t('admin.moderation.action_report')}</Option>
          </Select>
          <Select
            placeholder={t('messages.report_select_category')}
            onChange={(v) => setSelectedCategory(v as ReportReasonId)}
            value={selectedCategory || undefined}
          >
            {REPORT_REASON_IDS.map((id) => (
              <Option key={id} value={id}>
                <span className="conversation-report-dialog__category-option">
                  {t(`messages.report_reasons.${id}`)}
                </span>
              </Option>
            ))}
          </Select>
          <TextArea
            placeholder={t('messages.moderation_details_placeholder')}
            value={message}
            onChange={setMessage}
            rows={5}
          />
          <Button
            style={{
              color: getButtonTextColor(),
              backgroundColor: getButtonColor(),
            }}
            size={"default"}
            shape={"round"}
            onClick={handleSubmit}
          >
            {t('common.send_button')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
